import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button } from "antd";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { GET_STAFF_MANAGEMENT_WORKERS } from "graphql/query/staff-management-gql";
import { Tables } from "components/layout";
import { Localize } from "components/service";
import useDebounce from "components/use-hooks/use-debounce";
import StaffManagementConst from "components/staff-management/staff-management-const";
import StaffWorkersTableFilter from "components/staff-management/filters/staff-workers-table-filter";
import StaffManagementHooks from "components/staff-management/hooks";
import useStaffWorkerStatusSessions from "components/staff-management/hooks/use-staff-worker-status-sessions";
import useStaffWorkersForSorting from "components/staff-management/hooks/use-staff-workers-for-sorting";
import {
    getStaffWorkerSessionSummaries,
    getStaffWorkerStatusFilter,
    getStaffWorkersQueryState,
    sortStaffWorkersByHours,
    STAFF_WORKERS_PAGE_SIZE,
} from "components/staff-management/helpers/staff-workers-api-helper";
import StaffWorkersTableHelper from "components/staff-management/helpers/staff-workers-table-helper";
import {
    getStaffWorkersStatusFromSearch,
    updateStaffWorkersStatusSearch,
} from "components/staff-management/helpers/staff-workers-navigation-helper";


const WORKERS_ORDER_BY = [
    { column: "NAME", order: "ASC" },
    { column: "SURNAME", order: "ASC" },
    { column: "ID", order: "ASC" },
];

const EMPTY_WORKERS_MODEL = {
    data: [],
    paginatorInfo: { total: 0, currentPage: 1, perPage: STAFF_WORKERS_PAGE_SIZE },
};

const StaffManagementWorkersSubPage = ({ event }) => {
    const history = useHistory();
    const { search: routeSearch } = useLocation();
    const routeUrl = `${ StaffManagementConst.basePath }/${ event.id }/workers`;
    const match = useRouteMatch(`${ routeUrl }/page/:pageNum`);
    const parsedPage = match ? Number(match.params.pageNum) : 1;
    const validRequestedPage = Number.isInteger(parsedPage) && parsedPage > 0;
    const canonicalPagePath = !match || (validRequestedPage && parsedPage > 1);
    const requestedPage = validRequestedPage ? parsedPage : 1;
    const [ searchText, setSearchText ] = useState("");
    const [ roleID, setRoleID ] = useState();
    const [ subcontractorID, setSubcontractorID ] = useState();
    const status = getStaffWorkersStatusFromSearch(routeSearch);
    const [ hoursSort, setHoursSort ] = useState(null);
    const [ visibleWorkers, setVisibleWorkers ] = useState([]);
    const sortingByHours = !!hoursSort;
    const needsAllSessions = !!status || sortingByHours;
    const debouncedSearchText = useDebounce(searchText);
    const waitingForSearch = sortingByHours && searchText !== debouncedSearchText;
    const statusSessions = useStaffWorkerStatusSessions({ eeID: event.id, enabled: needsAllSessions });
    const statusFilter = useMemo(() => getStaffWorkerStatusFilter({
        status,
        sessions: statusSessions.sessions,
    }), [ status, statusSessions.sessions ]);
    const queryState = useMemo(() => getStaffWorkersQueryState({
        eventRoles: event.relWorkerRoles,
        selectedRoleID: roleID,
        subcontractorID,
        statusWhere: statusFilter.where,
    }), [ event.relWorkerRoles, roleID, subcontractorID, statusFilter.where ]);
    const holdStatusResults = !!status && (!statusSessions.ready || statusFilter.noMatches);
    const allWorkers = useStaffWorkersForSorting({
        enabled: sortingByHours && !holdStatusResults && !waitingForSearch,
        eeID: event.id,
        text: debouncedSearchText,
        where: queryState.where,
    });
    const { sessionSummaries: pageSessionSummaries } = StaffManagementHooks.useStaffWorkerSessions({
        eeID: event.id,
        workers: needsAllSessions ? [] : visibleWorkers,
    });
    const summaryWorkers = sortingByHours ? allWorkers.workers : visibleWorkers;
    const sessionSummaries = useMemo(() => needsAllSessions ?
        getStaffWorkerSessionSummaries(summaryWorkers, statusSessions.sessions) :
        pageSessionSummaries,
    [ needsAllSessions, summaryWorkers, statusSessions.sessions, pageSessionSummaries ]);
    const sortedWorkers = useMemo(() => sortingByHours ?
        sortStaffWorkersByHours(allWorkers.workers, sessionSummaries, hoursSort) : [],
    [ sortingByHours, allWorkers.workers, sessionSummaries, hoursSort ]);
    const sortedModel = useMemo(() => {
        if (!sortingByHours) return undefined;
        if (holdStatusResults || waitingForSearch || !statusSessions.ready || !allWorkers.ready) return EMPTY_WORKERS_MODEL;

        const offset = (requestedPage - 1) * STAFF_WORKERS_PAGE_SIZE;
        return {
            data: sortedWorkers.slice(offset, offset + STAFF_WORKERS_PAGE_SIZE),
            paginatorInfo: { total: sortedWorkers.length, currentPage: requestedPage, perPage: STAFF_WORKERS_PAGE_SIZE },
        };
    }, [ sortingByHours, holdStatusResults, waitingForSearch, statusSessions.ready, allWorkers.ready, sortedWorkers, requestedPage ]);
    const loadError = (needsAllSessions && statusSessions.error) || (sortingByHours && allWorkers.error);
    const tableHelper = useMemo(() => ({
        ...StaffWorkersTableHelper,
        columns: StaffWorkersTableHelper.columns.map(column => column.dataIndex === "totalHours" ? {
            ...column, key: "totalHours", sorter: true, sortOrder: hoursSort, sortDirections: [ "descend", "ascend" ],
        } : column),
    }), [ hoursSort ]);

    const resetPage = useCallback(() => {
        // A filter may have just changed the URL; read it live to keep its new status.
        if (history.location.pathname !== routeUrl)
        {
            history.push({ pathname: routeUrl, search: history.location.search });
        }
    }, [ history, routeUrl ]);

    const setStatus = useCallback((nextStatus) => {
        const search = updateStaffWorkersStatusSearch(history.location.search, nextStatus);
        if (history.location.pathname !== routeUrl || history.location.search !== search)
        {
            history.push({ pathname: routeUrl, search });
        }
    }, [ history, routeUrl ]);

    const onSortChange = ({ order }) => {
        const nextOrder = order || null;
        if (nextOrder === hoursSort) return;
        setHoursSort(nextOrder);
        resetPage();
    };
    const retry = () => {
        if (statusSessions.error) statusSessions.retry();
        if (allWorkers.error) allWorkers.retry();
    };

    useEffect(() => {
        if (!canonicalPagePath)
        {
            history.replace({ pathname: routeUrl, search: history.location.search });
        }
    }, [ canonicalPagePath, history, routeUrl ]);

    useEffect(() => {
        if (sortedModel && sortedModel !== EMPTY_WORKERS_MODEL &&
            requestedPage > Math.max(1, Math.ceil(sortedWorkers.length / STAFF_WORKERS_PAGE_SIZE)))
        {
            history.replace({ pathname: routeUrl, search: history.location.search });
        }
    }, [ sortedModel, sortedWorkers.length, requestedPage, history, routeUrl ]);

    useEffect(() => {
        if (roleID && !queryState.eligibleRoleIDs.includes(String(roleID)))
        {
            setRoleID(undefined);
            resetPage();
        }
    }, [ queryState.eligibleRoleIDs, resetPage, roleID ]);

    return (
        <div className="staff-management-view-shell--workers">
            <Tables.Main
                model="users"
                query={ GET_STAFF_MANAGEMENT_WORKERS }
                routeUrl={ routeUrl }
                routeSearch={ routeSearch }
                currentPage={ requestedPage }
                externalModel={ sortingByHours ? sortedModel : holdStatusResults ? EMPTY_WORKERS_MODEL : undefined }
                externalLoading={ (needsAllSessions && statusSessions.loading) ||
                    (sortingByHours && (waitingForSearch || allWorkers.loading)) }
                tableVisible={ !loadError }
                searchText={ searchText }
                objFilter={ queryState.where }
                objOrderBy={ WORKERS_ORDER_BY }
                currentObj={ { sessionSummaries, eeID: event.id } }
                onData={ setVisibleWorkers }
                onSortChange={ onSortChange }
                varSet={ {
                    perPage: STAFF_WORKERS_PAGE_SIZE,
                    eeID: [ String(event.id) ],
                } }
                tableHelper={ tableHelper }
                extraClass="staff-workers-table"
                scroll={ { x: 1368 } }>
                <StaffWorkersTableFilter
                    eventRoles={ event.relWorkerRoles || [] }
                    eventSubcontractors={ event.relWorkerSubcontractors || [] }
                    searchText={ searchText }
                    setSearchText={ setSearchText }
                    roleID={ roleID }
                    setRoleID={ setRoleID }
                    subcontractorID={ subcontractorID }
                    setSubcontractorID={ setSubcontractorID }
                    status={ status }
                    setStatus={ setStatus }
                    resetPage={ resetPage } />
                { loadError &&
                    <Alert
                        type="error"
                        showIcon
                        message={ <Localize>{ sortingByHours ?
                            "STAFF_MANAGEMENT.Error_LoadWorkers" : "STAFF_MANAGEMENT.Error_LoadWorkerStatuses" }</Localize> }
                        action={
                            <Button onClick={ retry }>
                                <Localize>STAFF_MANAGEMENT.Action_Retry</Localize>
                            </Button>
                        } />
                }
            </Tables.Main>
        </div>
    );
};

export default StaffManagementWorkersSubPage;
